import {
  Box,
  Badge,
  Tooltip,
  Button,
  VStack,
  Text,
  Icon,
  HStack,
} from '@chakra-ui/react'
import { LuLock, LuSparkles } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

interface PremiumFeatureProps {
  /** Whether this feature is available (not locked) */
  enabled: boolean
  /** Whether to display it in "premium / locked" mode vs fully hidden */
  premium?: boolean
  /** Content to render when enabled */
  children: ReactNode
  /** Optional label override for the badge */
  badgeLabel?: string
  /** Where to place the badge */
  badgePlacement?: 'top-right' | 'inline'
}

/**
 * Wraps any feature with premium gating logic:
 * - enabled=true  → renders children normally
 * - enabled=false, premium=true  → renders a locked overlay with upsell CTA
 * - enabled=false, premium=false → renders nothing
 */
export const PremiumFeature: React.FC<PremiumFeatureProps> = ({
  enabled,
  premium = true,
  children,
  badgeLabel,
  badgePlacement = 'top-right',
}) => {
  const { t } = useTranslation()
  const label = badgeLabel ?? t('chu.premium.badge')

  if (enabled) {
    return <>{children}</>
  }

  if (!premium) return null

  return (
    <Box position="relative">
      {/* Blurred / dimmed preview */}
      <Box
        filter="blur(2px)"
        opacity={0.35}
        pointerEvents="none"
        userSelect="none"
      >
        {children}
      </Box>

      {/* Lock overlay */}
      <Box
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="whiteAlpha.700"
        backdropFilter="blur(4px)"
        borderRadius="xl"
        zIndex={2}
      >
        <VStack spacing={2}>
          <Icon as={LuLock} boxSize={6} color="gray.500" />
          <Text fontSize="sm" fontWeight="semibold" color="gray.700" textAlign="center">
            {t('chu.premium.lockedTooltip')}
          </Text>
          <Button
            size="xs"
            colorScheme="purple"
            leftIcon={<Icon as={LuSparkles} boxSize={3} />}
            variant="solid"
          >
            {t('chu.premium.lockedCTA')}
          </Button>
        </VStack>
      </Box>

      {/* Premium badge */}
      {badgePlacement === 'top-right' && (
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme="purple"
          fontSize="2xs"
          px={2}
          py={0.5}
          borderRadius="full"
          zIndex={3}
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Icon as={LuSparkles} boxSize={2.5} />
          {label}
        </Badge>
      )}
    </Box>
  )
}

/** Lightweight inline badge — use next to section titles */
export const PremiumBadge: React.FC<{ label?: string }> = ({ label }) => {
  const { t } = useTranslation()
  return (
    <Tooltip label={t('chu.premium.lockedTooltip')} hasArrow placement="top">
      <Badge
        colorScheme="purple"
        fontSize="2xs"
        px={2}
        py={0.5}
        borderRadius="full"
        display="inline-flex"
        alignItems="center"
        gap={1}
        cursor="default"
      >
        <Icon as={LuSparkles} boxSize={2.5} />
        {label ?? t('chu.premium.badge')}
      </Badge>
    </Tooltip>
  )
}

/** Show a section heading with an optional premium badge */
export const PremiumSectionHeader: React.FC<{
  title: string
  subtitle?: string
  isPremium?: boolean
}> = ({ title, subtitle, isPremium }) => (
  <HStack spacing={2} align="baseline" mb={1}>
    <Text fontWeight="bold" fontSize="sm" color="gray.700">{title}</Text>
    {isPremium && <PremiumBadge />}
    {subtitle && (
      <Text fontSize="xs" color="gray.500">{subtitle}</Text>
    )}
  </HStack>
)
